import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useTabActive } from "../../lib/tabActive";
import { createPortal } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../../lib/trpc";
import { Icon } from "../ui/Icon";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { HardDeleteConfirm } from "../ui/HardDeleteConfirm";
import { Drawer } from "../Drawer";
import { SearchSelect, type SSOption } from "../ui/SearchSelect";
import { ToggleSwitch } from "../ui/ToggleSwitch";
import { warnText } from "./shiftWarn";
import "./ShiftWeekBoard.css";

/**
 * HAFTALIK VARDİYA PANOSU — satır = vardiya (+ istirahat), sütun = pozisyon, hücre = personel.
 * Kullanıcının kâğıt/Excel programının (MAYA FOOD haftalık vardiya programı) dijital karşılığı.
 *
 * Veri kaynaği `hr.shiftPlan.weekBoard`; o da aylık ızgarayla AYNI çözücüyü (resolveShiftGrid)
 * kullanır → haftalık ve aylık görünüm hiçbir koşulda ayrışamaz.
 *
 * "Kişi o hafta şu vardiyada" varsayımı sahadaki düzenden gelir. Hafta ortasında vardiyası
 * değişen personel EN ÇOK göründüğü vardiyaya konur ama `mixed` işaretlenir — sessizce tek
 * vardiyaya indirgenmez; ekran bunu göstermek ZORUNDADIR.
 */

const DOW_LABEL = ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"];
const OFF_KEY = "off";
const NO_POS = "none";
/** Bu sayıdan FAZLA kişi barındıran sütun iki kat genişler, listesi iki kola akar. */
const WIDE_AT = 10;
/** "Planı Genişlet" kaç hafta ileri dondurur (açık hafta dâhil). */
const EXPAND_WEEKS = 4;

/**
 * "Plan dışı tutulan N personel" şeridinin kapatılma durumu.
 *
 * MODÜL düzeyinde tutuluyor, bileşen state'inde değil: kullanıcı Haftalık↔Aylık sekmesi
 * arasında gidip gelince pano her seferinde yeniden kurulur ve bileşen state'i sıfırlanırdı
 * (kapattığı şerit geri gelirdi). Modül değişkeni sayfa YENİLENİNCE sıfırlanır — istenen
 * davranış tam olarak bu: "sayfayı yenileyene kadar kapalı kalsın".
 * localStorage BİLİNÇLİ OLARAK kullanılmadı; kalıcı olsaydı kişi sayısı değiştiğinde
 * kullanıcı yeni durumdan habersiz kalırdı.
 */
let hiddenBannerDismissed = false;

/**
 * TEK SEFERLİK DEĞİŞİM — çalışanların kendi aralarında anlaştığı takası yönetici kaydeder.
 * Kilitli haftada da çalışır (var oluş nedeni budur).
 *
 * Sunucu kuralı: iki taraf da o gün ÇALIŞIYOR olmalı. Biri istirahatteyken takas, birinde
 * fazla mesai diğerinde devamsızlık üretirdi — diyalog bunu önceden söyler, sunucu reddeder.
 */
function SwapDialog({
  days, employees, busy, error, onCancel, onApply,
}: {
  days: Array<{ date: string; dow: number }>;
  employees: Array<{ id: string; name: string; code: string | null; shiftId: string | null }>;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onApply: (date: string, aId: string, bId: string, reason: string) => void;
}) {
  const [date, setDate] = useState(days[0]?.date ?? "");
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("");
  const [reason, setReason] = useState("");
  // Yalnız o hafta ÇALIŞAN personel — istirahattekini listelemek reddedilecek seçim sunmak olurdu.
  const working = useMemo(() => employees.filter((e) => e.shiftId), [employees]);
  const valid = !!date && !!aId && !!bId && aId !== bId;

  return (
    <Drawer
      open onClose={onCancel} size="md" dismissible={!busy}
      title="Tek seferlik değişim"
      subtitle="İki personelin aynı gündeki vardiyası takas edilir"
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onCancel} disabled={busy}>Vazgeç</button>
          <button type="button" className="btn-md primary" disabled={busy || !valid} onClick={() => onApply(date, aId, bId, reason.trim())}>
            {busy ? "Kaydediliyor…" : "Değişimi Kaydet"}
          </button>
        </>
      }
    >
      <div className="form-banner-info">
        <Icon name="info" size={14} className="ebi-bnr-ic" />
        <span>
          Puantaj <b>etkilenmez</b>: iki kişi de o gün çalışmaya devam eder, yalnız vardiyaları yer değiştirir.
          Bu yüzden takas için <b>ikisinin de o gün çalışıyor olması</b> gerekir.
        </span>
      </div>
      {error ? <div className="form-banner-err" role="alert">{error}</div> : null}

      <div className="form-field">
        <label className="lbl" htmlFor="sw-date">Gün</label>
        <select id="sw-date" className="form-input" value={date} onChange={(e) => setDate(e.target.value)}>
          {days.map((x) => (
            <option key={x.date} value={x.date}>{DOW_LABEL[x.dow]} · {x.date.slice(8)}.{x.date.slice(5, 7)}</option>
          ))}
        </select>
      </div>
      <div className="form-row">
        <div className="form-field">
          <label className="lbl" htmlFor="sw-a">1. personel</label>
          <select id="sw-a" className="form-input" value={aId} onChange={(e) => setAId(e.target.value)}>
            <option value="">Seçin…</option>
            {working.map((e) => <option key={e.id} value={e.id} disabled={e.id === bId}>{e.name}{e.code ? ` (${e.code})` : ""}</option>)}
          </select>
        </div>
        <div className="form-field">
          <label className="lbl" htmlFor="sw-b">2. personel</label>
          <select id="sw-b" className="form-input" value={bId} onChange={(e) => setBId(e.target.value)}>
            <option value="">Seçin…</option>
            {working.map((e) => <option key={e.id} value={e.id} disabled={e.id === aId}>{e.name}{e.code ? ` (${e.code})` : ""}</option>)}
          </select>
        </div>
      </div>
      <div className="form-field">
        <label className="lbl" htmlFor="sw-reason">Gerekçe</label>
        <input id="sw-reason" className="form-input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} placeholder="örn. kendi aralarında anlaştılar" />
      </div>
    </Drawer>
  );
}

/**
 * Taşıma diyaloğu — kullanıcının 5. gereksinimi burada karşılanır:
 * "müdahale hafta başında DEĞİLSE, hafta başından itibaren mi yoksa o gün ve sonrasında mı?"
 *
 * Soru YALNIZ hafta başlamışsa sorulur. Hafta henüz başlamamışsa (gelecek hafta) tüm hafta
 * değişir ve soru anlamsızdır — sormak kullanıcıyı gereksiz karar vermeye zorlardı.
 * Varsayılan "bugünden itibaren": geçmiş günlerin puantajını geriye dönük değiştirmek
 * İSTENMEYEN taraftır, o yüzden güvenli olan öndedir.
 */
function MoveDialog({
  weekStart, weekEnd, days, shifts, empName, initialShiftId, busy, onCancel, onApply,
}: {
  weekStart: string; weekEnd: string;
  days: Array<{ date: string; dow: number }>;
  shifts: Array<{ id: string; name: string; code: string | null; startTime: string; endTime: string; isActive: boolean }>;
  empName: string;
  initialShiftId: string | null;
  busy: boolean;
  onCancel: () => void;
  onApply: (shiftId: string | null, scope: "week" | "fromDay", fromDate: string | undefined, reason: string) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const weekStarted = today > weekStart;          // hafta başladı mı
  const inWeek = today >= weekStart && today <= weekEnd;
  const defaultFrom = inWeek ? today : weekStart;
  const [shiftId, setShiftId] = useState<string | null>(initialShiftId);
  const [scope, setScope] = useState<"week" | "fromDay">(weekStarted ? "fromDay" : "week");
  const [fromDate, setFromDate] = useState(defaultFrom);
  const [reason, setReason] = useState("");

  return (
    <Drawer
      open onClose={onCancel} size="md" dismissible={!busy}
      title="Vardiya değiştir"
      subtitle={`${empName} · ${weekStart.slice(8)}.${weekStart.slice(5, 7)} – ${weekEnd.slice(8)}.${weekEnd.slice(5, 7)}`}
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onCancel} disabled={busy}>Vazgeç</button>
          <button
            type="button" className="btn-md primary" disabled={busy}
            onClick={() => onApply(shiftId, scope, scope === "fromDay" ? fromDate : undefined, reason.trim())}
          >
            {busy ? "Uygulanıyor…" : "Uygula"}
          </button>
        </>
      }
    >
      <div className="form-field">
        <label className="lbl" htmlFor="mv-shift">Yeni vardiya</label>
        <select id="mv-shift" className="form-input" value={shiftId ?? ""} onChange={(e) => setShiftId(e.target.value || null)}>
          <option value="">İstirahat (çalışma günleri izne çevrilir)</option>
          {shifts.filter((s) => s.isActive).map((s) => (
            <option key={s.id} value={s.id}>{s.code ? `${s.code} — ` : ""}{s.name} ({s.startTime}–{s.endTime})</option>
          ))}
        </select>
        <span className="help">Yalnız ÇALIŞMA günleri değişir; haftalık izin ve tatil günlerine dokunulmaz.</span>
      </div>

      {weekStarted ? (
        <div className="form-field">
          <span className="lbl">Ne zamandan itibaren?</span>
          <div className="seg" role="group" aria-label="Kapsam">
            <button type="button" className={`seg-btn ${scope === "fromDay" ? "active" : ""}`} onClick={() => setScope("fromDay")}>
              Seçilen günden itibaren
            </button>
            <button type="button" className={`seg-btn ${scope === "week" ? "active" : ""}`} onClick={() => setScope("week")}>
              Hafta başından itibaren
            </button>
          </div>
          {scope === "fromDay" ? (
            <select className="form-input" style={{ marginTop: 8 }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} aria-label="Başlangıç günü">
              {days.map((x) => (
                <option key={x.date} value={x.date}>
                  {DOW_LABEL[x.dow]} · {x.date.slice(8)}.{x.date.slice(5, 7)}
                </option>
              ))}
            </select>
          ) : null}
          <span className="help">
            {scope === "week"
              ? "Hafta başından bugüne kadarki günler de değişir — bu günlerin PUANTAJI da yeniden hesaplanır."
              : "Geçmiş günlere dokunulmaz; yalnız seçilen gün ve sonrası değişir."}
          </span>
        </div>
      ) : (
        <div className="form-banner-info">
          <Icon name="info" size={14} className="ebi-bnr-ic" />
          <span>Bu hafta henüz başlamadı — değişiklik <b>tüm haftaya</b> uygulanacak.</span>
        </div>
      )}

      <div className="form-field">
        <label className="lbl" htmlFor="mv-reason">Gerekçe</label>
        <input id="mv-reason" className="form-input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} placeholder="örn. hat arızası nedeniyle kaydırıldı" />
        <span className="help">Aylık ızgarada bu günün üzerine gelince görünür.</span>
      </div>
    </Drawer>
  );
}

function fmtDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}
/** Hafta başlığı: "27.07 – 02.08.2026" (yıl bir kez). */
function fmtRange(a: string, b: string): string {
  const [ya, ma, da] = a.split("-");
  const [yb, mb, db] = b.split("-");
  return ya === yb ? `${da}.${ma} – ${db}.${mb}.${yb}` : `${da}.${ma}.${ya} – ${db}.${mb}.${yb}`;
}

interface Props {
  /** Haftanın herhangi bir günü (YYYY-MM-DD); sunucu Pazartesi'ye hizalar. */
  weekStart: string;
  onWeekChange: (iso: string) => void;
  canManage: boolean;
}

type PlaceResult = { written: number; keptManual: number; employeeCount: number };

export function ShiftWeekBoard({ weekStart, onWeekChange, canManage }: Props) {
  const qc = useQueryClient();
  /**
   * DAR KAPSAMLI TAZELEME — argümansız `invalidateQueries()` DEĞİL.
   *
   * Buradaki mutasyonlar (hafta yerleştir, planı genişlet, planı temizle, toplu şablon)
   * YALNIZ vardiya planını ve onun türevi olan puantaj bayatlığını değiştirir. Argümansız
   * çağrı, açık HER SEKMEDEKİ her sorguyu tazeliyordu: 10 sekme açıkken tek bir "haftayı
   * yerleştir" işlemi 31 yordamı yeniden çekiyordu — stok, sipariş, sevkiyat dahil.
   *
   * NOT: Bu daraltma her yerde YAPILAMAZ. Örneğin QC'de lot serbest bırakmak stoğu,
   * siparişleri ve sevkiyatı GERÇEKTEN etkiler; orada geniş tazeleme DOĞRUDUR.
   * Ölçüt "çağrı geniş mi" değil, "mutasyonun etki alanı ne kadar" olmalı.
   */
  // tRPC'nin TİP GÜVENLİ yolu kullanılır — ham `queryKey` dizisi ELLE yazılsaydı anahtar
  // şekli tutmadığında tazeleme SESSİZCE hiçbir şeyle eşleşmez ve pano bayat kalırdı.
  // Depodaki yerleşik desen bu (ör. PositionsTab: qc.hr.position.invalidate()).
  const utils = trpc.useUtils();
  const invalShiftPlan = useCallback(() => {
    void utils.hr.shiftPlan.invalidate();
    void utils.hr.attendance.invalidate();
  }, [utils]);
  const q = trpc.hr.shiftPlan.weekBoard.useQuery({ weekStart }, { staleTime: 5_000 });
  const [placed, setPlaced] = useState<PlaceResult | null>(null);
  const [confirmPlace, setConfirmPlace] = useState(false);
  const placeMut = trpc.hr.shiftPlan.weekPlace.useMutation({
    onSuccess: (r) => { setPlaced({ written: r.written, keptManual: r.keptManual, employeeCount: r.employeeCount }); invalShiftPlan(); },
  });
  const inval = () => { invalShiftPlan(); };
  const assignMut = trpc.hr.shiftPlan.weekAssign.useMutation({ onSuccess: inval });
  const swapMut = trpc.hr.shiftPlan.swapDay.useMutation({ onSuccess: inval });
  const publishMut = trpc.hr.shiftPlan.weekPublish.useMutation({ onSuccess: inval });
  const lockMut = trpc.hr.shiftPlan.weekLock.useMutation({ onSuccess: inval });
  const reopenMut = trpc.hr.shiftPlan.weekReopen.useMutation({ onSuccess: inval });

  /** Taşıma diyaloğu: kimi, hangi vardiyaya. Kapsam sorusu hafta başında DEĞİLSE sorulur. */
  const [move, setMove] = useState<null | { empId: string; empName: string; toShiftId: string | null; toLabel: string }>(null);
  /** Sütunlar arası aktarım: kimi, hangi pozisyona (İK ana veri değişikliği → onay şart). */
  const [xfer, setXfer] = useState<null | { empId: string; empName: string; toPosId: string; toTitle: string; fromTitle: string }>(null);
  /** Çoklu seçim — toplu şablon ataması için. */
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [expandOpen, setExpandOpen] = useState(false);
  // GENİŞ KALIR: personelin pozisyonu VE birimi değişiyor; personel listeleri, org şeması,
  // pozisyon kadro sayıları ve bordro oran çözümü bundan etkilenir.
  const xferMut = trpc.hr.shiftPlan.assignPosition.useMutation({ onSuccess: () => { void qc.invalidateQueries(); } });
  const expandMut = trpc.hr.shiftPlan.weekPlaceRange.useMutation({ onSuccess: () => { invalShiftPlan(); } });
  const [hiddenClosed, setHiddenClosed] = useState(hiddenBannerDismissed);


  /* ── Dışa aktarım ────────────────────────────────────────────────────────────
     PDF için jsPDF gibi bir bağımlılık EKLENMEDİ: tarayıcının kendi yazdırma yolu
     metni VEKTÖR bırakır (seçilebilir, yazıcıda net), canvas'ı PDF'e gömmek ise
     rasterleştirip kaliteyi düşürürdü. JPEG tarafında zaten raster istendiği için
     html2canvas kullanılıyor — üretim raporundaki desenin aynısı, ölçek 3 (daha kaliteli).
     İkisi de TAM EKRAN üzerinden çalışır: pano o modda <body> altına portal edilir,
     yazdırma kuralları da bu sayede "gövdenin panodan başka çocuklarını gizle" diyebilir. */
  /** Tam ekran DOM'u portala taşıdığı için bir kare beklemek şart. */

  /**
   * PDF → AYRI BASKI SAYFASI (/print/vardiya-plani), panonun kendisi DEĞİL.
   *
   * Panoyu yazdırmayı denedik, olmadı: pano bir ekran widget'ı — 158px'lik sütunlar,
   * kaydırma kutusu, tek hücrede 86 kişi. A4 yatayda kullanılabilir genişlik ~277mm;
   * pano 2000px'i aşınca sayfaya sığmıyor, ölçekleyince okunmuyordu. Baskı sayfası
   * AKAN yerleşim kullanır (vardiya bölümleri + çok kolonlu isim listeleri), kendiliğinden
   * sayfalanır ve metin vektör kalır. Depodaki diğer /print/* belgeleriyle aynı desen.
   */
  const exportPdf = () => {
    if (!d) return;
    window.open(`/print/vardiya-plani?weekStart=${encodeURIComponent(d.weekStart)}`, "_blank", "noopener");
  };

  /**
   * JPEG — BASKI SAYFASINDA üretilir, panoda değil.
   * Panonun CSS'i `color-mix(in srgb, …)` kullanıyor; html2canvas 1.4.1 bu fonksiyonu
   * tanımıyor ve "Attempting to parse an unsupported color function" ile çöküyordu.
   * Baskı sayfasının paleti düz hex — sorun kökten kalkıyor, üstelik çıktı sayfaya
   * sığdırılmış profesyonel yerleşim oluyor.
   */
  const exportJpeg = () => {
    if (!d) return;
    window.open(`/print/vardiya-plani?weekStart=${encodeURIComponent(d.weekStart)}&export=jpeg`, "_blank", "noopener");
  };
  const [clearOpen, setClearOpen] = useState(false);
  const clearMut = trpc.hr.shiftPlan.planClear.useMutation({ onSuccess: () => { invalShiftPlan(); setClearOpen(false); } });
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropKey, setDropKey] = useState<string | null>(null);
  const [swapOpen, setSwapOpen] = useState(false);
  const d = q.data;

  /* ── Sayfa içi arama ─────────────────────────────────────────────────────────
     Panoda 100+ çip var; birini gözle bulmak zor. Yazdıkça eşleşenler vurgulanır,
     eşleşmeyenler soluklaşır (gizlenmez — kişinin hangi sütun/vardiyada olduğu
     bağlamıyla birlikte görünmeli). Türkçe katlama şart: `toLowerCase()` "İ"yi
     birleşik noktalı harfe çevirir ve "ilker" araması "İlker"i bulamaz. */
  const [find, setFind] = useState("");
  const foldTr = (x: string) =>
    x.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/ı/g, "i");
  const hits = useMemo(() => {
    const qy = foldTr(find.trim());
    if (!qy || !d) return null;                       // null = arama kapalı
    const set = new Set<string>();
    for (const e of d.employees) {
      if (foldTr(`${e.name} ${e.code ?? ""}`).includes(qy)) set.add(e.id);
    }
    return set;
  }, [find, d]);

  /* ── Tam ekran ───────────────────────────────────────────────────────────────
     Sayfa kabuğu (başlık + sekme çubuğu + araç şeridi + ay bantları) ~300px dikey
     yer tutuyor; tam ekran bunları kaldırıp tabloya viewport'un tamamını verir.
     Portal <body>'ye: `position: fixed` bir ata `transform`/`contain` taşırsa
     yerinde kalınca kırılırdı (Drawer/ProcessingOverlay ile aynı gerekçe). */
  const [fs, setFs] = useState(false);
  const tabActive = useTabActive();
  const fsRef = useRef<HTMLDivElement | null>(null);
  const fsOpenerRef = useRef<HTMLElement | null>(null);
  // PASİF SEKMEDE TAM EKRAN KAPANIR. Katman `document.body`ye portal edilir ve `#root`u
  // `inert` yapar; sekme gizlense bile ikisi de ayakta kalır → kullanıcı diğer sekmede
  // HİÇBİR ŞEYE tıklayamazdı. Sekmeye dönünce tam ekranı yeniden açar (tek tık),
  // buna karşılık uygulamanın kilitlenmesi geri alınamaz bir arıza olurdu.
  useEffect(() => {
    if (!tabActive && fs) setFs(false);
  }, [tabActive, fs]);
  useEffect(() => {
    if (!fs || !tabActive) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Üstte açık bir çekmece/diyalog varsa ESC ÖNCE onu kapatmalı; tam ekranı
      // birlikte kapatmak kullanıcının bir tuşla iki katman kaybetmesi olurdu.
      if (document.querySelector(".drawer") || document.querySelector(".cdlg-backdrop")) return;
      setFs(false);
    };
    document.addEventListener("keydown", onKey);
    // Katman görüntü alanını TAMAMEN kapattığı için arkadaki uygulama görsel olarak yok;
    // `inert` ile ekran okuyucu ve Tab sırası için de yok edilir. Aksi hâlde kullanıcı
    // göremediği bir arayüzde geziniyor olurdu. Portal <body> altında olduğundan
    // (#root'un KARDEŞİ) kendisi inert'ten etkilenmez; Drawer'lar da öyle.
    const appRoot = document.getElementById("root");
    if (appRoot) appRoot.inert = true;
    // Odak: portal DOM'u yeniden kurulduğu için odak <body>'ye düşer → katmana al,
    // çıkışta açan butona geri ver.
    const restoreTo = fsOpenerRef.current;
    fsRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      if (appRoot) appRoot.inert = false;
      restoreTo?.focus();
    };
  }, [fs]);
  // NOT: body scroll kilidi BİLİNÇLİ OLARAK YOK — Drawer kapanırken body.overflow'u
  // kendisi sıfırlıyor, iki sahip olsaydı biri diğerini bozardı. Sabit katman zaten
  // görüntü alanını tamamen kaplıyor.

  /* ── Dikeyde sığdırma (auto-fit) ─────────────────────────────────────────────
     Tablo taşıyorsa yoğunluğu kademe kademe sıkıştır: 0 → 1 → 2. Yazı boyutu son
     kademeye kadar korunur; önce boşluk feda edilir. Kademe yalnız ARTAR ve 2'de
     durur → döngü imkânsız. Veri/tam-ekran değişince 0'a döner (kalabalık hafta
     geçtikten sonra sıkışık kalmasın). */
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [density, setDensity] = useState(0);
  useEffect(() => { setDensity(0); }, [d, fs]);
  // Pencere yeniden boyutlandığında 0'a dön: kademe yalnız artabildiği için, ekran
  // büyüdüğünde tablo gereksiz yere sıkışık kalırdı. Sıfırlamanın ardından aşağıdaki
  // ölçüm gerekiyorsa yeniden kademeliyor (gerekmiyorsa 0'da kalır).
  useEffect(() => {
    const onResize = () => setDensity(0);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el || density >= 2) return;
    // 4px tolerans: alt kenarlık/yuvarlama yüzünden 1-2px taşma sıkıştırmayı tetiklemesin.
    if (el.scrollHeight > el.clientHeight + 4) setDensity((v) => v + 1);
  });

  /* ── Şerit tek satırda kalsın ────────────────────────────────────────────────
     Sekiz buton dar ekranda alt satıra kaçıyordu. Sığmayınca etiketler gizlenir,
     butonlar ikona düşer (metin DOM'da KALIR → ekran okuyucu okumaya devam eder,
     `font-size: 0` yalnız görseldir; ayrıca hepsinde `title` var).

     HİSTEREZİS ŞART: "sığmıyorsa daralt" tek başına salınıma yol açar — daraltınca
     sığar, sığınca genişletiriz, sonsuz döngü. Bu yüzden GENİŞ hâlin doğal genişliği
     saklanır ve ancak o kadar yer AÇILDIĞINDA geri dönülür.
     Viewport medya sorgusu yerine ResizeObserver: pano hem sayfa içinde (kenar çubuğu
     kadar dar) hem tam ekranda çalışıyor, ikisinde de doğru ölçüm gerekiyor. */
  const barRef = useRef<HTMLDivElement | null>(null);
  /** Kademe başına ÖLÇÜLEN doğal genişlik — geri açarken kullanılır. */
  const barW = useRef<number[]>([]);
  const [barLevel, setBarLevel] = useState(0);   // 0: tam etiket · 1: yardımcılar ikon · 2: hepsi ikon
  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const measure = () => {
      // `scrollWidth` HER durumda o kademenin gerçek içerik genişliğidir (nowrap).
      // Yalnız "sığdığında" kaydetmek hataydı: kademe 1'de sığmadığı için genişliği
      // hiç kaydedilmiyor, 2'den 1'e geri dönüş imkânsız hâle geliyordu.
      barW.current[barLevel] = el.scrollWidth;
      const fits = el.scrollWidth <= el.clientWidth + 2;
      if (!fits && barLevel < 2) { setBarLevel(barLevel + 1); return; }
      // Geri açılış: BİR ALT kademenin ölçülmüş genişliği sığıyorsa dön. Ölçülmemişse
      // (hiç o kademede bulunmadıysak) dokunma — tahminle salınım başlatmayalım.
      const prev = barW.current[barLevel - 1];
      if (barLevel > 0 && prev && el.clientWidth > prev + 24) setBarLevel(barLevel - 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [barLevel, d, sel.size, fs]);

  const shiftById = useMemo(
    () => new Map((d?.shifts ?? []).map((s) => [s.id, s])),
    [d?.shifts],
  );
  const empById = useMemo(
    () => new Map((d?.employees ?? []).map((e) => [e.id, e])),
    [d?.employees],
  );
  const cellMap = useMemo(
    () => new Map((d?.cells ?? []).map((c) => [c.key, c.employeeIds])),
    [d?.cells],
  );

  /** Sütunlar: pozisyonlar + (varsa) pozisyonsuz kovası EN SONDA. */
  const columns = useMemo(() => {
    const cols = (d?.positions ?? []).map((p) => ({ id: p.id, title: p.title }));
    if (d?.hasNoPosition) cols.push({ id: NO_POS, title: "(pozisyon atanmamış)" });
    return cols;
  }, [d?.positions, d?.hasNoPosition]);

  /** Satırlar: yalnız BU HAFTA kullanılan vardiyalar + istirahat. Boş vardiya satırı gösterilmez. */
  type BoardRow = { key: string; shift: (typeof d extends undefined ? never : NonNullable<typeof d>["shifts"][number]) | null };
  const rows = useMemo<BoardRow[]>(() => {
    if (!d) return [];
    const used = new Set<string>();
    for (const c of d.cells) used.add(c.key.split("|")[0]!);
    const list: BoardRow[] = d.shifts.filter((s) => used.has(s.id)).map((s) => ({ key: s.id, shift: s }));
    if (used.has(OFF_KEY)) list.push({ key: OFF_KEY, shift: null });
    return list;
  }, [d]);

  /**
   * KALABALIK SÜTUN: bir kolda en fazla WIDE_AT kişi durur; fazlası YENİ KOLA taşar.
   * 86 kişilik bir hücre tek kolda tabloyu metrelerce uzatıyordu; 9 kolda 10 satır yüksekliğe
   * iner. Genişlik yatayda kaydırılabilir, yükseklik ise ekranı kilitliyordu.
   *
   * Kol sayısı SÜTUN bazında hesaplanır (hücre bazında değil): tablo bir sütunun genişliğini
   * TEK bir değerle çözer, hücre başına karar vermek satırdan satıra oynayan sütun demekti.
   * Bu yüzden sütunun EN KALABALIK hücresi belirleyicidir.
   */
  const colSpan = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of columns) {
      let max = 0;
      for (const r of rows) max = Math.max(max, cellMap.get(`${r.key}|${c.id}`)?.length ?? 0);
      m.set(c.id, Math.max(1, Math.ceil(max / WIDE_AT)));
    }
    return m;
  }, [columns, rows, cellMap]);

  function shiftWeek(deltaDays: number) {
    if (!d) return;
    setPlaced(null);
    const base = new Date(`${d.weekStart}T00:00:00Z`);
    base.setUTCDate(base.getUTCDate() + deltaDays);
    onWeekChange(base.toISOString().slice(0, 10));
  }
  function thisWeek() {
    setPlaced(null);
    onWeekChange(new Date().toISOString().slice(0, 10));
  }

  /** Taşıma diyaloğunu aç. `toShiftId` verilmezse (tıklama yolu) hedef diyalogda seçilir. */
  function openMove(empId: string, toShiftId?: string | null) {
    const e = empById.get(empId);
    if (!e || !canManage || locked) return;
    const label = toShiftId === undefined
      ? ""
      : toShiftId === null ? "İstirahat" : (shiftById.get(toShiftId)?.name ?? "");
    setMove({ empId, empName: e.name, toShiftId: toShiftId === undefined ? null : toShiftId, toLabel: label });
  }

  const mixedCount = (d?.employees ?? []).filter((e) => e.mixed).length;
  const plannedCount = (d?.employees ?? []).filter((e) => e.shiftId).length;
  /** Haftanın kendi kilidi VEYA haftanın ayının kilidi — ikisi de yazmayı durdurur. */
  const weekLocked = d?.weekPeriod?.status === "locked";
  const monthLocked = d?.period?.status === "locked";
  const locked = weekLocked || monthLocked;
  const weekStatus = (d?.weekPeriod?.status ?? "draft") as "draft" | "published" | "locked";

  const board = (
    <div
      className={`swb-root${fs ? " swb-fs" : ""}${density > 0 ? ` swb-d${density}` : ""}`}
      ref={fsRef}
      {...(fs ? { role: "dialog" as const, "aria-modal": true, "aria-label": "Haftalık vardiya panosu — tam ekran", tabIndex: -1 } : {})}
    >
      <div className={`swb-bar${barLevel > 0 ? ` c${barLevel}` : ""}`} ref={barRef}>
        <button type="button" className="btn-md ghost sm" onClick={() => shiftWeek(-7)} disabled={q.isLoading} aria-label="Önceki hafta" title="Önceki hafta">
          <Icon name="chevLeft" size={15} />
        </button>
        <button type="button" className="btn-md ghost sm" onClick={() => shiftWeek(7)} disabled={q.isLoading} aria-label="Sonraki hafta" title="Sonraki hafta">
          <Icon name="chevRight" size={15} />
        </button>
        <button type="button" className="btn-md secondary sm" onClick={thisWeek} disabled={q.isLoading} title="İçinde bulunulan haftaya dön">Bu Hafta</button>
        {/* KÜNYE İKİ SATIR: tek satırda özet "…istiraha…" diye kırpılıyordu.
            Üstte hafta aralığı, altta özet + durum rozeti. Butonlar yine tek satır —
            künye kendi içinde iki satır, şeridin akışını bozmuyor. */}
        {d ? (
          <span className="swb-meta">
            <span className="swb-range">{fmtRange(d.weekStart, d.weekEnd)}</span>
            <span className="swb-line2">
              <span className="swb-sub">
                <span className="mono">{plannedCount}</span> planlı ·{" "}
                <span className="mono">{d.employees.length - plannedCount}</span> istirahat/plansız
                {mixedCount > 0 ? <> · <span className="mono">{mixedCount}</span> hafta içi değişen</> : null}
              </span>
              <span className={`swb-per ${weekStatus}`}>
                <Icon name={weekStatus === "locked" ? "lock" : weekStatus === "published" ? "checkCircle" : "edit"} size={13} />
                {weekStatus === "locked" ? "Kilitli" : weekStatus === "published" ? "Yayınlandı" : "Taslak"}
              </span>
            </span>
          </span>
        ) : null}
        <span className="grow" />

        {/* ── Aksiyon grupları ──────────────────────────────────────────────────
            Tek sırada 6 buton, yıkıcı olanla yayınlama olanı yan yana getiriyordu.
            Ayırıcılarla dört öbek: GÖRÜNÜM | PLAN KUR | YIKICI | YAYINLA.
            Temizle ayrıca `danger` varyantı — renk TEK BAŞINA değil, ikon + ayrı öbek. */}
        <span className="swb-find">
          <Icon name="search" size={14} className="ic" />
          <input
            type="search" value={find} onChange={(e) => setFind(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); setFind(""); } }}
            placeholder="Panoda ara…" aria-label="Panoda personel ara"
          />
          {hits ? <span className={`cnt${hits.size === 0 ? " zero" : ""}`}>{hits.size}</span> : null}
        </span>

        <span className="swb-grp">
          {/* Görünür metin erişilebilir ADIN kendisidir — ayrıca aria-label vermek
              "Label in Name" ihlali olurdu (sesle "Küçült" diyen kullanıcı butonu bulamaz). */}
          <button
            type="button" className="btn-md secondary sm"
            onClick={(e) => { if (!fs) fsOpenerRef.current = e.currentTarget; setFs((v) => !v); }}
            aria-pressed={fs}
            title={fs ? "Tam ekrandan çık (Esc)" : "Tam ekran — tabloya tüm ekranı ver"}
          >
            <Icon name={fs ? "minimize" : "maximize"} size={15} /><span className="ico-lbl">{fs ? "Küçült" : "Tam Ekran"}</span>
          </button>
          <button
            type="button" className="btn-md secondary sm"
            onClick={exportPdf} disabled={!d}
            title="Vardiya programını yeni sekmede aç — içeriği seçip A4 dikey yazdır ya da PDF kaydet"
          >
            <Icon name="printer" size={15} /><span className="ico-lbl">PDF</span>
          </button>
          <button
            type="button" className="btn-md secondary sm"
            onClick={exportJpeg} disabled={!d}
            title="Vardiya programını yeni sekmede aç ve yüksek çözünürlüklü JPEG olarak indir"
          >
            <Icon name="image" size={15} /><span className="ico-lbl">JPEG</span>
          </button>
        </span>

        {canManage && d ? (
          <>
            <span className="swb-grp">
              {sel.size > 0 ? (
                <button type="button" className="btn-md primary sm" onClick={() => setBulkOpen(true)} title={`Seçili ${sel.size} personele vardiya şablonu ata`}>
                  <Icon name="users" size={14} />{sel.size} kişiye şablon ata
                </button>
              ) : null}
              {sel.size > 0 ? (
                <button type="button" className="btn-md ghost sm" onClick={() => setSel(new Set())} title="Seçimi temizle">Seçimi bırak</button>
              ) : null}
              <button
                type="button" className="btn-md secondary sm"
                onClick={() => setConfirmPlace(true)}
                disabled={placeMut.isPending || locked}
                title={locked ? (weekLocked ? "Bu hafta kilitli" : "Bu ay kilitli") : "Şablonlardan bu haftanın planını oluştur"}
              >
                <Icon name="zap" size={14} />{placeMut.isPending ? "Yerleştiriliyor…" : "Haftayı Yerleştir"}
              </button>
              <button
                type="button" className="btn-md secondary sm"
                onClick={() => { expandMut.reset(); setExpandOpen(true); }}
                disabled={expandMut.isPending}
                title="Sonraki haftaları şablondan dondur — sonradan şablon değişse bile bu haftalar değişmez"
              >
                <Icon name="calendar" size={14} />{expandMut.isPending ? "Genişletiliyor…" : "Planı Genişlet"}
              </button>
              <button
                type="button" className="btn-md secondary sm"
                onClick={() => { swapMut.reset(); setSwapOpen(true); }}
                disabled={swapMut.isPending || monthLocked}
                title="İki personelin aynı gündeki vardiyasını takas et (kilitli haftada da çalışır)"
              >
                <Icon name="shuffle" size={14} />Tek Seferlik Değişim
              </button>
            </span>

            <span className="swb-grp">
              <button
                type="button" className="btn-md danger sm"
                onClick={() => { clearMut.reset(); setClearOpen(true); }}
                disabled={clearMut.isPending || locked}
                title={locked ? "Kilitli — önce kilidi açın" : "Bu haftanın yazılı planını sil; hafta yeniden şablondan hesaplanır"}
              >
                <Icon name="trash" size={14} />Haftayı Temizle
              </button>
            </span>

            <span className="swb-grp">
              {weekStatus === "draft" ? (
                <button type="button" className="btn-md primary sm" disabled={publishMut.isPending || monthLocked} onClick={() => publishMut.mutate({ weekStart })} title="Haftayı yayınla — ekipler görebilir">
                  <Icon name="upload" size={14} />Yayınla
                </button>
              ) : weekStatus === "published" ? (
                <button type="button" className="btn-md secondary sm" disabled={lockMut.isPending} onClick={() => lockMut.mutate({ weekStart })} title="Haftayı kilitle — yalnız tek seferlik değişim kalır">
                  <Icon name="lock" size={14} />Kilitle
                </button>
              ) : (
                <button type="button" className="btn-md secondary sm" disabled={reopenMut.isPending} onClick={() => reopenMut.mutate({ weekStart })} title="Haftanın kilidini aç">
                  <Icon name="lock" size={14} />Kilidi Aç
                </button>
              )}
            </span>
          </>
        ) : null}
      </div>

      {/* ELEME SESSİZ OLMAZ: kimin neden plana girmediği yazılı olmalı, yoksa kullanıcı
          "personelim kayboldu" diye arar. Sıfırsa hiç çizilmez; kullanıcı kapatabilir. */}
      {d && !hiddenClosed && (d.hidden.noPosition + d.hidden.nonShiftPosition + d.hidden.nonShiftUnit) > 0 ? (
        <div className="form-banner-info swb-dismissible">
          <Icon name="info" size={14} className="ebi-bnr-ic" />
          <span>
            Plan dışı tutulan <b>{d.hidden.noPosition + d.hidden.nonShiftPosition + d.hidden.nonShiftUnit} personel</b>:{" "}
            {[
              d.hidden.nonShiftPosition > 0 ? `${d.hidden.nonShiftPosition} kişi vardiyalı olmayan pozisyonda` : null,
              d.hidden.noPosition > 0 ? `${d.hidden.noPosition} kişinin pozisyonu atanmamış` : null,
              d.hidden.nonShiftUnit > 0 ? `${d.hidden.nonShiftUnit} kişi vardiya dışı birimde` : null,
            ].filter(Boolean).join(" · ")}
            . Pozisyonu vardiyalı yapmak için <b>Pozisyonlar</b> sekmesindeki “Vardiyalı” anahtarını açın.
            Puantaj ve bordro bu kişileri görmeye devam eder.
          </span>
          <button
            type="button" className="swb-bnr-x"
            aria-label="Bilgilendirmeyi kapat"
            title="Kapat (sayfayı yenileyince geri gelir)"
            onClick={() => { hiddenBannerDismissed = true; setHiddenClosed(true); }}
          >
            <Icon name="x" size={13} />
          </button>
        </div>
      ) : null}

      {weekLocked ? (
        <div className="form-banner-info">
          <Icon name="lock" size={14} className="ebi-bnr-ic" />
          <span>
            Bu hafta <b>kilitli</b> — plan donduruldu. Çalışanların kendi aralarında anlaştığı değişimi
            <b> tek seferlik değişim</b> ile kaydedebilirsiniz: iki kişinin aynı gündeki vardiyası takas edilir,
            puantajda fazla mesai ya da devamsızlık oluşmaz.
          </span>
        </div>
      ) : null}
      {monthLocked ? (
        <div className="form-banner-err" role="alert">
          <Icon name="lock" size={14} className="ebi-bnr-ic" />
          <span>Bu haftanın ayı kilitli (bordro onaylanmış) — plan değiştirilemez.</span>
        </div>
      ) : null}
      {assignMut.error ? <div className="form-banner-err" role="alert">{assignMut.error.message}</div> : null}
      {swapMut.error ? <div className="form-banner-err" role="alert">{swapMut.error.message}</div> : null}
      {publishMut.error ? <div className="form-banner-err" role="alert">{publishMut.error.message}</div> : null}
      {lockMut.error ? <div className="form-banner-err" role="alert">{lockMut.error.message}</div> : null}
      {reopenMut.error ? <div className="form-banner-err" role="alert">{reopenMut.error.message}</div> : null}
      {swapMut.data ? (
        <div className="form-banner-ok">
          <Icon name="check" size={14} className="ebi-bnr-ic" />
          <span>
            Değişim kaydedildi ({fmtDate(swapMut.data.date)}): <b>{swapMut.data.a.from} ↔ {swapMut.data.b.from}</b>.
            {swapMut.data.warnings.length > 0 ? ` ${swapMut.data.warnings.join(" ")}` : " Puantaj etkilenmedi."}
          </span>
        </div>
      ) : null}

      {xferMut.data?.changed ? (
        <div className="form-banner-ok">
          <Icon name="check" size={14} className="ebi-bnr-ic" />
          <span>
            <b>{xferMut.data.name}</b> → <b>{xferMut.data.toTitle}</b> pozisyonuna aktarıldı
            {xferMut.data.orgUnitMoved ? ", organizasyon birimi de güncellendi" : ""}.
            {xferMut.data.leavesPlan ? " DİKKAT: bu pozisyon vardiyalı değil, kişi plandan düştü." : ""}
          </span>
        </div>
      ) : null}
      {clearMut.data ? (
        <div className="form-banner-ok">
          <Icon name="check" size={14} className="ebi-bnr-ic" />
          <span>
            Hafta temizlendi — <b>{clearMut.data.deleted}</b> gün satırı silindi
            {Object.keys(clearMut.data.bySource ?? {}).length > 0
              ? ` (${Object.entries(clearMut.data.bySource).map(([k, v]) => `${k}: ${v}`).join(", ")})`
              : ""}. Hafta artık şablondan hesaplanıyor.
          </span>
        </div>
      ) : null}
      {expandMut.error ? <div className="form-banner-err" role="alert">{expandMut.error.message}</div> : null}
      {expandMut.data ? (
        <div className={expandMut.data.skipped.length > 0 ? "form-banner-warn" : "form-banner-ok"}>
          <Icon name={expandMut.data.skipped.length > 0 ? "alertTriangle" : "check"} size={14} className="ebi-bnr-ic" />
          <span>
            <b>{expandMut.data.placed} hafta</b> donduruldu (<b>{expandMut.data.totalWritten}</b> gün satırı
            {expandMut.data.totalKeptManual > 0 ? `, ${expandMut.data.totalKeptManual} elle gün korundu` : ""}).
            {expandMut.data.skipped.length > 0
              ? ` Atlanan: ${expandMut.data.skipped.map((s) => `${fmtDate(s.weekStart)} — ${s.reason}`).join(" · ")}`
              : ""}
          </span>
        </div>
      ) : null}

      {placeMut.error ? <div className="form-banner-err" role="alert">{placeMut.error.message}</div> : null}
      {placed ? (
        <div className="form-banner-ok">
          <Icon name="check" size={14} className="ebi-bnr-ic" />
          <span>
            Hafta yerleştirildi — <b>{placed.written}</b> gün yazıldı, <b>{placed.employeeCount}</b> personel.
            {placed.keptManual > 0 ? <> <b>{placed.keptManual}</b> elle konulmuş gün korundu (üzerine yazılmadı).</> : null}
          </span>
        </div>
      ) : null}

      {q.isLoading ? (
        <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
      ) : q.error ? (
        <div className="form-banner-err" role="alert">{q.error.message}</div>
      ) : !d || rows.length === 0 ? (
        <div className="empty-state">
          <p className="ttl">Bu hafta için plan yok</p>
          <p className="sub">Personele vardiya şablonu atayın ya da “Toplu Ata” ile rotasyon verin; plan bu panoda görünür.</p>
        </div>
      ) : (
        <>
          {mixedCount > 0 ? (
            <div className="form-banner-info">
              <Icon name="alertTriangle" size={14} className="ebi-bnr-ic" />
              <span>
                <b>{mixedCount} personelin</b> vardiyası hafta içinde değişiyor. Panoda en çok
                bulundukları vardiyada gösteriliyorlar ve <b>sarı</b> işaretliler — gün gün görmek için Aylık sekmesini kullanın.
              </span>
            </div>
          ) : null}

          <div className="swb-wrap" ref={wrapRef}>
            <table className="swb-tbl">
              <thead>
                <tr>
                  <th className="swb-rowhead" scope="col">Vardiya</th>
                  {columns.map((c) => {
                    const n = rows.reduce((s, r) => s + (cellMap.get(`${r.key}|${c.id}`)?.length ?? 0), 0);
                    const span = colSpan.get(c.id) ?? 1;
                    // Sütundaki HERKESİ tek tikle seç — 86 kişiyi tek tek işaretlemek işkenceydi.
                    const colIds = rows.flatMap((r) => cellMap.get(`${r.key}|${c.id}`) ?? []);
                    const allPicked = colIds.length > 0 && colIds.every((id) => sel.has(id));
                    const somePicked = colIds.some((id) => sel.has(id));
                    return (
                      <th key={c.id} scope="col" title={c.title} style={{ "--swb-cols": span } as CSSProperties}>
                        <span className="hd">
                          {canManage && colIds.length > 0 ? (
                            <input
                              type="checkbox" className="pick"
                              checked={allPicked}
                              ref={(el) => { if (el) el.indeterminate = !allPicked && somePicked; }}
                              aria-label={`${c.title}: tümünü seç`}
                              title={allPicked ? "Seçimi kaldır" : "Tümünü seç"}
                              onChange={() => setSel((p) => {
                                const nx = new Set(p);
                                if (allPicked) colIds.forEach((id) => nx.delete(id));
                                else colIds.forEach((id) => nx.add(id));
                                return nx;
                              })}
                            />
                          ) : null}
                          <span className="ttl">{c.title}</span>
                        </span>
                        <span className="cnt">{n} kişi</span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const total = columns.reduce((s, c) => s + (cellMap.get(`${r.key}|${c.id}`)?.length ?? 0), 0);
                  return (
                    <tr key={r.key} className={r.shift ? undefined : "swb-row-off"}>
                      <td className="swb-rowhead">
                        <div className="swb-sh">
                          <span className="nm">
                            {r.shift ? (
                              <>
                                {r.shift.code ? <span className="badge-pill b-primary mono">{r.shift.code}</span> : null}
                                {r.shift.name}
                              </>
                            ) : (
                              <><Icon name="clock" size={13} />İstirahat</>
                            )}
                          </span>
                          {r.shift ? <span className="tm">{r.shift.startTime}–{r.shift.endTime}</span> : null}
                          <span className="tot">{total} kişi</span>
                        </div>
                      </td>
                      {columns.map((c) => {
                        const cellKey = `${r.key}|${c.id}`;
                        const ids = cellMap.get(cellKey) ?? [];
                        // Kırmızı işaret = bırakmanın HİÇBİR ETKİSİ olmayacağı hedef:
                        // aynı vardiya + aynı pozisyon, ya da "pozisyonsuz" kovası (oraya
                        // taşımak bir pozisyon SİLME işlemi olurdu, sürükleyerek yapılmaz).
                        const dragEmp = dragId ? empById.get(dragId) : null;
                        const sameRow = !!dragEmp && (dragEmp.shiftId ?? OFF_KEY) === r.key;
                        const sameCol = !!dragEmp && (dragEmp.positionId ?? NO_POS) === c.id;
                        const noEffect = !!dragEmp && ((sameRow && sameCol) || (!sameCol && c.id === NO_POS));
                        return (
                          <td
                            key={c.id}
                            className={dropKey === cellKey ? (noEffect ? "drop-bad" : "drop") : undefined}
                            style={{ "--swb-cols": colSpan.get(c.id) ?? 1 } as CSSProperties}
                            onDragOver={(ev) => { if (dragId && canManage && !locked) { ev.preventDefault(); setDropKey(cellKey); } }}
                            onDragLeave={() => setDropKey((k) => (k === cellKey ? null : k))}
                            onDrop={(ev) => {
                              ev.preventDefault();
                              setDropKey(null);
                              if (!dragId) return;
                              const emp = empById.get(dragId);
                              setDragId(null);
                              if (!emp) return;
                              // SÜTUN değiştiyse bu bir BİRİM AKTARIMIDIR (pozisyon değişikliği),
                              // vardiya taşıma değil. İki işi tek sürüklemede birleştirmiyoruz:
                              // biri İK ana verisini, diğeri haftalık planı değiştirir; tek onayda
                              // ikisini birden yapmak kullanıcının beklemediği bir sonuç üretir.
                              const fromCol = emp.positionId ?? NO_POS;
                              if (c.id !== fromCol) {
                                if (c.id === NO_POS) return;           // "pozisyonsuz" kovasına taşınamaz
                                setXfer({
                                  empId: emp.id, empName: emp.name, toPosId: c.id, toTitle: c.title,
                                  fromTitle: columns.find((x) => x.id === fromCol)?.title ?? "—",
                                });
                                return;
                              }
                              if (sameRow) return;
                              openMove(emp.id, r.key === OFF_KEY ? null : r.key);
                            }}
                          >
                            {ids.length === 0 ? (
                              <div className="swb-empty">—</div>
                            ) : (
                              // Çipleri saran kap: iki kola akıtmak için gerekli (CSS multi-column
                              // doğrudan <td> üzerinde güvenilir çalışmaz).
                              <div className="swb-cell">
                              {ids.map((id) => {
                                const e = empById.get(id);
                                if (!e) return null;
                                const warn = e.warnings.length > 0;
                                return (
                                  <button
                                    key={id}
                                    type="button"
                                    // Sürüklemek TEK yol DEĞİL: tıklamak aynı diyaloğu açar
                                    // (klavye + dokunmatik kullanıcı sürükleyemez).
                                    draggable={canManage && !locked}
                                    disabled={!canManage || locked}
                                    onDragStart={() => setDragId(id)}
                                    onDragEnd={() => { setDragId(null); setDropKey(null); }}
                                    onClick={() => openMove(id)}
                                    className={`swb-chip${e.mixed ? " mixed" : warn ? " warn" : ""}${dragId === id ? " dragging" : ""}${sel.has(id) ? " picked" : ""}${hits ? (hits.has(id) ? " hit" : " miss") : ""}`}
                                  >
                                    {canManage ? (
                                      // Seçim kutusu çipin İÇİNDE ama ayrı bir tıklama hedefi:
                                      // çipe tıklamak "taşı" diyaloğunu açar, kutuya tıklamak seçer.
                                      // <span role="checkbox"> değil gerçek <input> — klavye ve ekran
                                      // okuyucu davranışını tarayıcıdan alalım.
                                      <input
                                        type="checkbox" className="pick"
                                        checked={sel.has(id)}
                                        aria-label={`${e.name} seç`}
                                        onClick={(ev) => ev.stopPropagation()}
                                        onChange={(ev) => {
                                          ev.stopPropagation();
                                          setSel((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
                                        }}
                                      />
                                    ) : null}
                                    {e.mixed ? <Icon name="alertTriangle" size={12} className="ic" /> : warn ? <Icon name="info" size={12} className="ic" /> : null}
                                    <span className="nm">{e.name}</span>
                                    {/* Dar çipte ad "…" ile kesiliyor; tam ad üzerine gelince burada
                                        çıkar. Native `title` KULLANILMIYOR — ikisi birden olsaydı iki
                                        ayrı balon üst üste binerdi ve native olan ~1sn gecikmeli. */}
                                    <span className="pop" role="tooltip">
                                      {e.name}{e.code ? ` · ${e.code}` : ""}
                                      {e.mixed ? " · hafta içinde vardiyası değişiyor" : ""}
                                      {warn ? ` · ${e.warnings.map(warnText).join(", ")}` : ""}
                                    </span>
                                  </button>
                                );
                              })}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="ebi-note">
            Hafta: {fmtDate(d.weekStart)} – {fmtDate(d.weekEnd)} ({d.days.map((x) => DOW_LABEL[x.dow]).join(" ")}).
            Pano haftalık düzeni gösterir; gün bazlı istisnalar Aylık sekmesinde görünür.
          </p>
        </>
      )}

      {swapOpen && d ? (
        <SwapDialog
          days={d.days}
          employees={d.employees}
          busy={swapMut.isPending}
          error={swapMut.error?.message ?? null}
          onCancel={() => setSwapOpen(false)}
          onApply={(date, aId, bId, reason) => {
            swapMut.mutate(
              { date, employeeAId: aId, employeeBId: bId, reason: reason || null },
              { onSuccess: () => setSwapOpen(false) },
            );
          }}
        />
      ) : null}

      {move && d ? (
        <MoveDialog
          weekStart={d.weekStart}
          weekEnd={d.weekEnd}
          days={d.days}
          shifts={d.shifts}
          empName={move.empName}
          initialShiftId={move.toShiftId}
          busy={assignMut.isPending}
          onCancel={() => setMove(null)}
          onApply={(shiftId, scope, fromDate, reason) => {
            assignMut.mutate(
              { employeeId: move.empId, weekStart: d.weekStart, shiftId, scope, fromDate, reason: reason || null },
              { onSuccess: () => setMove(null) },
            );
          }}
        />
      ) : null}

      {/* BİRİM AKTARIMI onayı — İK ana verisi değişiyor, sürükleyip bırakmak tek başına yetmez. */}
      <ConfirmDialog
        open={!!xfer}
        title="Personeli bu pozisyona aktar?"
        message={
          <>
            <b>{xfer?.empName}</b> kalıcı olarak <b>{xfer?.fromTitle}</b> → <b>{xfer?.toTitle}</b> pozisyonuna geçirilecek.
            <br />
            Yeni pozisyonun bağlı olduğu <b>organizasyon birimi de personele yazılır</b>.
            Geçmiş puantaj ve bordro kayıtları DEĞİŞMEZ; panodaki geçmiş haftalar yeni sütunda görünür.
            {xferMut.error ? <><br /><br /><span className="err">{xferMut.error.message}</span></> : null}
          </>
        }
        tone="primary"
        confirmLabel="Aktar"
        cancelLabel="Vazgeç"
        busy={xferMut.isPending}
        onConfirm={() => {
          if (!xfer) return;
          xferMut.mutate({ employeeId: xfer.empId, positionId: xfer.toPosId }, { onSuccess: () => setXfer(null) });
        }}
        onCancel={() => { xferMut.reset(); setXfer(null); }}
      />

      {bulkOpen && d ? (
        <BulkPatternDialog
          count={sel.size}
          weekStart={d.weekStart}
          employeeIds={[...sel]}
          onClose={() => setBulkOpen(false)}
          onDone={() => { setBulkOpen(false); setSel(new Set()); invalShiftPlan(); }}
        />
      ) : null}

      {/* YIKICI: elle konulmuş / Excel'den gelen günler de silinir → çift onay.
          Plan kaybolmaz, şablondan yeniden hesaplanır; ama elle girilen istisnalar gider. */}
      <HardDeleteConfirm
        open={clearOpen}
        code="TEMİZLE"
        title="Haftanın yazılı planını temizle"
        warning={
          <>
            <b>{d ? fmtRange(d.weekStart, d.weekEnd) : "Bu hafta"}</b> için yazılmış TÜM gün satırları silinecek —
            şablondan gelenler <b>ve elle konulmuş / Excel'den aktarılmış olanlar dâhil</b>.
            <br /><br />
            Hafta bundan sonra şablondan <b>canlı hesaplanır</b>: plan kaybolmaz, yeniden üretilir.
            Ama elle girdiğiniz istisnalar <b>geri alınamaz</b>.
            <br /><br />
            Puantaj ve bordro kayıtları bu işlemden etkilenmez.
          </>
        }
        busy={clearMut.isPending}
        errorMessage={clearMut.error?.message ?? null}
        onConfirmed={() => clearMut.mutate({ scope: "week", date: weekStart })}
        onClose={() => { clearMut.reset(); setClearOpen(false); }}
      />

      <ConfirmDialog
        open={expandOpen}
        title="Planı genişlet"
        message={
          <>
            Bu haftadan başlayarak <b>{EXPAND_WEEKS} hafta</b> şablonlardan <b>dondurulacak</b>.
            <br />
            Gelecek haftalar zaten şablondan canlı hesaplanıyor; dondurmanın kattığı şey,
            şablon ya da atama sonradan değişse bile <b>bu haftaların değişmemesi</b>.
            Elle konulmuş günlere dokunulmaz, kilitli haftalar atlanır ve raporlanır.
          </>
        }
        tone="primary"
        confirmLabel="Genişlet"
        cancelLabel="Vazgeç"
        busy={expandMut.isPending}
        onConfirm={() => { setExpandOpen(false); expandMut.mutate({ weekStart, weeks: EXPAND_WEEKS }); }}
        onCancel={() => setExpandOpen(false)}
      />

      <ConfirmDialog
        open={confirmPlace}
        title="Haftayı yerleştir?"
        message={
          <>
            {d ? <><b>{fmtRange(d.weekStart, d.weekEnd)}</b> haftasının planı </> : "Bu haftanın planı "}
            personelin vardiya şablonlarından yeniden oluşturulacak.
            <br />
            <b>Elle konulmuş gün istisnalarına dokunulmaz</b> — yalnız şablondan gelen günler yenilenir.
            Aynı işlemi tekrar çalıştırmak zarar vermez.
          </>
        }
        tone="primary"
        confirmLabel="Yerleştir"
        cancelLabel="Vazgeç"
        busy={placeMut.isPending}
        onConfirm={() => { setConfirmPlace(false); setPlaced(null); placeMut.mutate({ weekStart }); }}
        onCancel={() => setConfirmPlace(false)}
      />
    </div>
  );

  // Tam ekranda portal: sabit katman ata zincirinden bağımsız olsun (bir atada
  // `transform`/`contain` varsa `position: fixed` yerinde kırılırdı).
  // DİKKAT: portal hedefi değiştiği için React alt ağacın DOM'unu YENİDEN KURAR.
  // Bileşen state'i (açık diyalog, sürüklenen çip) korunur; DOM'a bağlı şeyler —
  // kaydırma konumu ve odak — SIFIRLANIR. Odak yukarıdaki effect ile geri veriliyor.
  return fs ? createPortal(board, document.body) : board;
}

/**
 * TOPLU ŞABLON ATAMA — panoda seçilen kişilere tek seferde rotasyon şablonu verir.
 *
 * Sunucudaki `assignmentBulk` ucunu kullanır (yeni uç YAZILMADI); tek farkı başlangıcın
 * PANODA AÇIK OLAN HAFTANIN Pazartesi'si olması — kullanıcının baktığı hafta ile yazdığı
 * plan aynı olsun diye. Geçmiş haftalara dokunmaz.
 *
 * "Sırayla dağıt" ekipleri A=0, B=1, C=2 … diye kaydırır; kullanıcının rotasyon sırasını
 * (A-B-C mi B-C-A mi) belirlemesinin yolu budur.
 */
function BulkPatternDialog({
  count, weekStart, employeeIds, onClose, onDone,
}: {
  count: number;
  weekStart: string;
  employeeIds: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const patternsQ = trpc.hr.shiftPlan.patternList.useQuery(undefined, { staleTime: 60_000 });
  const [patternId, setPatternId] = useState<string | null>(null);
  /**
   * Kayma stratejisi. VARSAYILAN "match": şablonu herkesin ŞU ANKİ vardiyasına oturtur,
   * rotasyon oradan devam eder. "spread" liste sırasına göre dağıtır (kimin nerede
   * çalıştığıyla ilgisi yok), "zero" ise herkesi aynı kaymaya koyar — 3 vardiyalı
   * düzende tüm fabrikayı tek ekip yapar, bu yüzden varsayılan DEĞİL.
   */
  const [mode, setMode] = useState<"match" | "spread" | "zero">("match");
  const mut = trpc.hr.shiftPlan.assignmentBulk.useMutation();

  const sugQ = trpc.hr.shiftPlan.suggestOffsets.useQuery(
    { patternId: patternId ?? "", weekStart, employeeIds },
    { enabled: mode === "match" && !!patternId, staleTime: 10_000 },
  );

  const pattern = (patternsQ.data ?? []).find((p) => p.id === patternId) ?? null;
  const cycle = Math.max(1, pattern?.cycleLength ?? 1);
  const opts = useMemo<SSOption[]>(
    () => (patternsQ.data ?? []).map((p) => ({
      value: p.id, label: p.name,
      desc: `${p.stepUnit === "week" ? "haftalık" : "günlük"} · ${p.cycleLength} adım`,
    })),
    [patternsQ.data],
  );

  const apply = () => {
    if (!patternId) return;
    const byId = new Map((sugQ.data?.rows ?? []).map((r) => [r.employeeId, r.cycleOffset]));
    mut.mutate({
      patternId,
      startDate: weekStart,
      closePrevious: true,
      entries: employeeIds.map((id, i) => ({
        employeeId: id,
        cycleOffset: mode === "match" ? (byId.get(id) ?? 0) : mode === "spread" ? i % cycle : 0,
      })),
    }, { onSuccess: (r) => { if (r.skipped.length === 0) onDone(); } });
  };
  const busyMatch = mode === "match" && !!patternId && sugQ.isLoading;

  return (
    <Drawer
      open onClose={onClose} size="md" dismissible={!mut.isPending}
      title="Seçilenlere şablon ata"
      subtitle={`${count} personel · ${fmtDate(weekStart)} haftasından itibaren`}
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={mut.isPending}>Kapat</button>
          <button type="button" className="btn-md primary" disabled={!patternId || mut.isPending || busyMatch} onClick={apply}>
            {mut.isPending ? "Atanıyor…" : busyMatch ? "Hesaplanıyor…" : `${count} personele ata`}
          </button>
        </>
      }
    >
      <></>
        <div className="form-field">
          <label className="lbl" htmlFor="bp-pattern">Vardiya Şablonu</label>
          <SearchSelect id="bp-pattern" value={patternId} options={opts} onChange={setPatternId}
            placeholder="Şablon seçin" emptyText="Tanımlı şablon yok" />
          {pattern ? (
            <span className="help">
              {pattern.cycleLength} adımlı döngü. “Sırayla dağıt” açıkken ilk kişi 0, ikinci 1 … diye kayar
              ({cycle} kişide başa döner) — ekiplerin birbirini takip etmesi böyle sağlanır.
            </span>
          ) : null}
        </div>

        <div className="form-field">
          <label className="lbl" htmlFor="bp-mode">Rotasyon kayması</label>
          <select id="bp-mode" className="form-select" value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
            <option value="match">Şu anki vardiyalarına göre hizala (önerilen)</option>
            <option value="spread">Sırayla dağıt (A=0, B=1, C=2…)</option>
            <option value="zero">Hepsi aynı kaymada (0)</option>
          </select>
          <span className="help">
            {mode === "match"
              ? "Herkes bu hafta BULUNDUĞU vardiyada kalır; sonraki haftalarda şablonun sırasına göre birlikte kayar. Gece'dekiler haftaya Ara'ya geçer."
              : mode === "spread"
                ? "Kayma listedeki sıraya göre verilir — kişinin şu an hangi vardiyada çalıştığıyla ilgisi yoktur."
                : "TÜM seçilenler aynı kaymayı alır, yani her hafta hep birlikte aynı vardiyaya düşerler. Çok vardiyalı düzende bunu genelde istemezsiniz."}
          </span>
        </div>

        {mode === "match" && sugQ.data ? (
          <div className={sugQ.data.unmatched > 0 ? "form-banner-warn" : "form-banner-ok"}>
            <Icon name={sugQ.data.unmatched > 0 ? "alertTriangle" : "check"} size={14} className="ebi-bnr-ic" />
            <span>
              <b>{sugQ.data.matched}</b> kişi mevcut vardiyasına oturtuldu
              {sugQ.data.unmatched > 0
                ? ` · ${sugQ.data.unmatched} kişinin bu haftaki vardiyası şablonda yok (istirahatte ya da farklı bir vardiyada) — onlara kayma 0 verilir.`
                : "."}
              {" "}Dağılım:{" "}
              {[...new Set((sugQ.data.rows ?? []).map((r) => r.cycleOffset))].sort((a, b) => a - b)
                .map((o) => `kayma ${o}: ${sugQ.data!.rows.filter((r) => r.cycleOffset === o).length} kişi`)
                .join(" · ")}
            </span>
          </div>
        ) : null}

        <div className="form-banner-info">
          <Icon name="info" size={14} className="ebi-bnr-ic" />
          <span>
            Atama <b>{fmtDate(weekStart)}</b> tarihinden itibaren geçerli olur ve kişinin önceki açık uçlu
            ataması bu tarihte kapatılır. Geçmiş haftalar etkilenmez.
          </span>
        </div>

        {mut.error ? <div className="form-banner-err" role="alert">{mut.error.message}</div> : null}
        {mut.data && mut.data.skipped.length > 0 ? (
          <div className="form-banner-warn" role="alert">
            <Icon name="alertTriangle" size={14} className="ebi-bnr-ic" />
            <span>
              <b>{mut.data.created}</b> atandı, <b>{mut.data.skipped.length}</b> atlandı:{" "}
              {mut.data.skipped.map((s) => `${s.code ?? s.employeeId.slice(0, 6)} (${s.reason})`).join(" · ")}
            </span>
          </div>
        ) : null}
    </Drawer>
  );
}
